// cspell:words xlarge
// Courier registry: a new courier = a CourierType value + a COURIERS entry (compiler-enforced).
export enum CourierType {
  UBER = 'uber-direct',
  ROADIE = 'roadie-xlarge',
  ROADIE_HUGE = 'roadie-huge',
  BUNGII = 'bungii',
  PARTNER = 'resupply-partner',
}

export type ExternalCourier = Exclude<CourierType, CourierType.PARTNER>;

/** Copy rendered as-is (no string templates). */
interface CourierCopy {
  /** Edit donation "Quote" card body; HTML (bold segments), followed by the new-pickup link. */
  quoteCard: string;
  /** Courier tab warning while the courier is on; omit when it needs no guaranteed items. */
  guaranteedItems?: string;
  /** Cancel dialog refund note; omit for none. Fees must match `CourierDetails#cancellation_fee` in resupply-api. */
  cancel?: { driverEnRoute: string; beforeDriver: string };
}

export interface CourierLogo {
  readonly src: string;
  readonly alt: string;
  readonly className: string;
}

interface CourierConfig {
  /** Family name: charity list column and filters; couriers sharing it filter together. */
  shortName: string;
  /** Charity courier tab toggle. */
  toggleLabel: string;
  /** Offered in the charity list courier filter. */
  charityFilter: boolean;
  /** Full name: courier panel, donations table, pickup type ("Courier (Uber Direct)"). */
  name: string;
  /** Small-item bag/box counts (Uber) instead of the booked catalog items. */
  smallItemsOnly: boolean;
  /** Donor self-loads: donor-load question and the verbal release of liability. */
  donorLoad: boolean;
  showVehicle: boolean;
  /** Captain sees the store zip as the dropoff passkey. */
  zipPasskey: boolean;
  /** Captain sees the courier's cancellation notes in the courier panel. */
  cancellationNotes: boolean;
  tipping: boolean;
  /** Lead wizard Order Review logo. */
  logo: CourierLogo;
  copy: CourierCopy;
}

const BOOKED_ITEMS_QUOTE =
  'This pickup was scheduled as a courier pickup. This means that the donor can donate <b>the items booked for this pickup</b> at no additional charge. If the donor wishes to add or change items, they will have to schedule a new pickup request with this link:';

const ROADIE_LOGO: CourierLogo = { src: 'assets/icons/roadie-logo.png', alt: 'Roadie', className: 'roadie-logo' };

const ROADIE_CANCEL = {
  driverEnRoute: "The donor will be refunded the full amount paid minus Roadie's $10 cancellation fee.",
  beforeDriver: 'The donor will be refunded the full amount paid.',
};

const ROADIE_GUARANTEED_ITEMS = 'Roadie requires guaranteed items — set them in the Items tab, or no pickup will qualify.';

const COURIERS: Record<ExternalCourier, CourierConfig> = {
  [CourierType.UBER]: {
    name: 'Uber Direct',
    shortName: 'Uber',
    toggleLabel: 'Uber',
    charityFilter: true,
    smallItemsOnly: true,
    donorLoad: false,
    showVehicle: true,
    zipPasskey: false,
    cancellationNotes: false,
    tipping: true,
    logo: { src: 'assets/icons/uber_logo.png', alt: 'Uber', className: 'uber-logo' },
    copy: {
      quoteCard:
        'This pickup was scheduled as a courier pickup. This means that the donor can donate up to a total of <b>5 bags/boxes</b> at no additional charge. If the donor wishes to add items outside that range or any large furniture items, they will have to schedule a new pickup request with this link:',
    },
  },
  [CourierType.ROADIE]: {
    name: 'Roadie',
    shortName: 'Roadie',
    toggleLabel: 'Roadie XL',
    charityFilter: true,
    smallItemsOnly: false,
    donorLoad: false,
    showVehicle: true,
    zipPasskey: true,
    cancellationNotes: false,
    tipping: true,
    logo: ROADIE_LOGO,
    copy: {
      quoteCard:
        'This pickup was scheduled as a courier pickup. This means that the donor can donate between <b>6 and 10 bags/boxes</b> at no additional charge. If the donor wishes to add items outside that range or any large furniture items, they will have to schedule a new pickup request with this link:',
      guaranteedItems: ROADIE_GUARANTEED_ITEMS,
      cancel: ROADIE_CANCEL,
    },
  },
  [CourierType.ROADIE_HUGE]: {
    name: 'Roadie',
    shortName: 'Roadie',
    toggleLabel: 'Roadie Huge',
    charityFilter: false,
    smallItemsOnly: false,
    donorLoad: true,
    showVehicle: true,
    zipPasskey: true,
    cancellationNotes: false,
    tipping: true,
    logo: ROADIE_LOGO,
    copy: {
      quoteCard:
        'This pickup was scheduled as a courier pickup. This means that the donor can donate <b>large items</b> at no additional charge. If the donor wishes to add or change items, they will have to schedule a new pickup request with this link:',
      guaranteedItems: ROADIE_GUARANTEED_ITEMS,
      cancel: ROADIE_CANCEL,
    },
  },
  [CourierType.BUNGII]: {
    name: 'Bungii',
    shortName: 'Bungii',
    toggleLabel: 'Bungii',
    charityFilter: true,
    smallItemsOnly: false,
    donorLoad: false,
    showVehicle: false,
    zipPasskey: false,
    cancellationNotes: true,
    tipping: false,
    logo: { src: 'assets/icons/bungii-logo.webp', alt: 'Bungii', className: 'bungii-logo' },
    copy: {
      quoteCard: BOOKED_ITEMS_QUOTE,
      guaranteedItems: 'Bungii requires guaranteed items — set them in the Items tab, or no pickup will qualify.',
      cancel: {
        driverEnRoute: "The donor will be refunded the full amount paid minus Bungii's $40 cancellation fee.",
        beforeDriver: 'The donor will be refunded the full amount paid.',
      },
    },
  },
};

/** Registry order: courier tab toggles, filters and the `couriers` array sent to courier_setup. */
const COURIER_LIST: (CourierConfig & { value: ExternalCourier })[] = (Object.keys(COURIERS) as ExternalCourier[]).map((value) => ({
  ...COURIERS[value],
  value,
}));

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

/** Everything a donation/lead screen may know about its courier, resolved — components read this, never COURIERS. */
export interface CourierRules {
  /** Third-party courier (anything but resupply-partner). */
  readonly isCourier: boolean;
  /** Full name ("Uber Direct"); undefined for a partner pickup. */
  readonly name?: string;
  /** Small-item bag/box counts (Uber) instead of the named item list. */
  readonly smallItemsOnly: boolean;
  readonly showVehicle: boolean;
  /** Store zip shown as the dropoff passkey (Roadie). */
  readonly showZipPasskey: boolean;
  /** Courier cancellation notes shown in the captain courier panel (Bungii). */
  readonly showCancellationNotes: boolean;
  readonly tipping: boolean;
  /** Donor self-loads (Roadie Huge): the agent confirms the verbal release of liability. */
  readonly requiresRelease: boolean;
  /** Edit donation "Quote" card body (HTML). */
  readonly quoteCard?: string;
  /** Lead wizard Order Review logo; undefined for a partner pickup. */
  readonly logo?: CourierLogo;
}

const PARTNER_RULES: CourierRules = deepFreeze({
  isCourier: false,
  smallItemsOnly: false,
  showVehicle: true,
  showZipPasskey: false,
  showCancellationNotes: false,
  tipping: false,
  requiresRelease: false,
});

/** Resolved once at load and frozen: stable identity for change detection, and no component can change them. */
const COURIER_RULES = COURIER_LIST.reduce(
  (all, courier) => ({
    ...all,
    [courier.value]: deepFreeze({
      isCourier: true,
      name: courier.name,
      smallItemsOnly: courier.smallItemsOnly,
      showVehicle: courier.showVehicle,
      showZipPasskey: courier.zipPasskey,
      showCancellationNotes: courier.cancellationNotes,
      tipping: courier.tipping,
      requiresRelease: courier.donorLoad,
      quoteCard: courier.copy.quoteCard,
      logo: { ...courier.logo },
    }),
  }),
  {} as Record<ExternalCourier, CourierRules>,
);

const getCourierConfig = (courier?: string | null): CourierConfig | undefined => {
  if (!courier || courier === CourierType.PARTNER) return undefined;
  const config = COURIERS[courier as ExternalCourier];
  if (!config) throw new Error(`Unknown courier "${courier}" — add it to CourierType and COURIERS`);
  return config;
};

/** Rules for a courier value; partner / no courier gets the partner rules. Throws on a courier missing from COURIERS. */
export const getCourierRules = (courier?: string | null): CourierRules =>
  getCourierConfig(courier) ? COURIER_RULES[courier as ExternalCourier] : PARTNER_RULES;

const PRIORITY_LOGO: CourierLogo = { src: 'assets/icons/priority-pickup.png', alt: 'Priority', className: 'priority-logo' };

/** Lead wizard Order Review logo `<img>`: the courier's, or Priority for a partner pickup. */
export const getCourierLogoHtml = (courier?: string | null): string => {
  const logo = getCourierRules(courier).logo ?? PRIORITY_LOGO;
  return `<img class='${logo.className}' src='${logo.src}' alt='${logo.alt}' />`;
};

/** Cancel dialog refund note: a courier's fee applies once the driver is en route to (or at) the pickup. */
export const getCourierCancelNote = (courier: string | null | undefined, partnerState: string | null | undefined): string | undefined => {
  const cancel = getCourierConfig(courier)?.copy.cancel;
  if (!cancel) return undefined;
  return partnerState === 'en_routed' || partnerState === 'arrived' ? cancel.driverEnRoute : cancel.beforeDriver;
};

/** Family name for lists and filters ("Roadie" for both tiers); partner / empty passes through. Throws on an unknown courier. */
export const getCourierShortName = (courier: string): string => getCourierConfig(courier)?.shortName ?? courier;

/** Charity Courier Settings toggles, in registry order. */
export const getCourierToggles = (): { value: ExternalCourier; label: string; guaranteedItems?: string }[] =>
  COURIER_LIST.map((courier) => ({ value: courier.value, label: courier.toggleLabel, guaranteedItems: courier.copy.guaranteedItems }));

/** Charity list courier filter options. */
export const getCharityFilterCouriers = (): { value: ExternalCourier; name: string }[] =>
  COURIER_LIST.filter((courier) => courier.charityFilter).map((courier) => ({ value: courier.value, name: courier.shortName }));

/** Donations "Pickup Courier" filter: one option per family, covering every courier in it. */
export const getCourierFamilies = (): { value: CourierType[]; name: string }[] =>
  COURIER_LIST.reduce(
    (families, courier) => {
      const family = families.find((option) => option.name === courier.shortName);
      if (family) family.value.push(courier.value);
      else families.push({ value: [courier.value], name: courier.shortName });
      return families;
    },
    [] as { value: CourierType[]; name: string }[],
  );

/** The charity offers this courier. Uber falls back to the legacy `courier` boolean (`Charity#uber_enabled?`). */
export const charityOffersCourier = (
  charity: { courier?: boolean; couriers?: string[] } | null | undefined,
  courier: ExternalCourier,
): boolean => (charity?.couriers ? charity.couriers.includes(courier) : courier === CourierType.UBER && !!charity?.courier);

/** The charity offers a donor-load courier (Roadie Huge) — gates the donor-load question. */
export const charityOffersDonorLoadCourier = (charity: { courier?: boolean; couriers?: string[] } | null | undefined): boolean =>
  COURIER_LIST.some((courier) => courier.donorLoad && charityOffersCourier(charity, courier.value));
